/* eslint-disable @typescript-eslint/naming-convention */
import {fetch} from '@shopify/cli-kit/node/http';

import {getBugsnag} from './bugsnag.js';
import {loadPackageJson} from './utils.js';

interface GodogEvent {
  name: string;
  type: string;
  value: number;
  tags: string[];
}

type MetricCollection = Record<
  string,
  [{value: number; tags: Record<string, string>}]
>;

interface MetricsExporterOptions {
  ciProvider: string;
  rootPath: string;
}

export enum MetricName {
  BuildTime = 'build_time',
  NumFilesRequested = 'num_files_requested',
  NumFilesUploaded = 'num_files_uploaded',
  TotalSizeUploaded = 'total_size_uploaded',
  TotalSizeRequested = 'total_size_requested',
  TotalTime = 'total_time',
  UploadTime = 'upload_time',
  DeploymentCompletedTime = 'deployment_completed_time',
  DeploymentRoutableTime = 'deployment_routable_time',
}

export class MetricsExporter {
  private metrics: MetricCollection = {};
  private readonly metricPrefix: string = 'oxygen.cli';
  private readonly defaultTags: Record<string, string>;
  private readonly exportUrl: string;

  constructor(options: MetricsExporterOptions) {
    const {version: cliVersion} = loadPackageJson();
    const {version: hydrogenVersion} = loadPackageJson(
      `${options.rootPath}/node_modules/@shopify/hydrogen`,
    );
    this.defaultTags = {
      ci_provider: options.ciProvider,
      cli_version: `oxygen-cli@${cliVersion || 'unknown'}`,
      hydrogen_version: hydrogenVersion || 'unknown',
    };
    this.exportUrl = 'https://metrics.myshopify.dev';
  }

  add(
    name: MetricName,
    value: number,
    tags: Record<string, string> = {},
  ): void {
    const metric = {
      value,
      tags,
    };

    if (this.metrics[name]) {
      this.metrics[name]!.push(metric);
    } else {
      this.metrics[name] = [metric];
    }
  }

  formatTags(tags: Record<string, string>): string[] {
    return Object.entries(tags).map(([key, value]) => `${key}:${value}`);
  }

  async export(): Promise<void> {
    const metricsToExport: GodogEvent[] = Object.entries(this.metrics)
      .map(([metricName, values]) => {
        const name = `${this.metricPrefix}.${metricName}`;

        return values.map(({value, tags}) => {
          return {
            name,
            type: 'gauge',
            value,
            tags: this.formatTags({...tags, ...this.defaultTags}),
          };
        });
      })
      .flat();

    try {
      await fetch(this.exportUrl, {
        method: 'POST',
        body: JSON.stringify({events: metricsToExport}),
      });
    } catch (error) {
      const Bugsnag = getBugsnag();
      if (Bugsnag && (error instanceof Error || typeof error === 'string')) {
        Bugsnag.notify(error);
      }
    }
  }
}
